import React, {
  useRef,
  useState,
  useEffect,
  useCallback
} from 'react';

import ActionButtons from './ActionButtons';
import VideoInfo from './VideoInfo';
import CommentDrawer from './CommentDrawer';
import { videoAPI } from '../services/api';

function VideoCard({
  video,
  isActive,
  isNearActive = false,
  requireAuth
}) {
  const videoRef = useRef(null);

  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [likes, setLikes] = useState(video.likes || 0);
  const [isLiked, setIsLiked] = useState(
    video.is_liked || false
  );

  const [showHeart, setShowHeart] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const [showLeftTray, setShowLeftTray] =
    useState(false);

  const leftTrayTimer = useRef(null);

  const tapTimer = useRef(null);
  const tapCount = useRef(0);
  const longPressTimer = useRef(null);

  /*
   * Only the active video plays.
   *
   * Adjacent videos are allowed to retain their
   * source, while videos outside the active window
   * have their source removed by the feed.
   */
  useEffect(() => {
    const element = videoRef.current;

    if (!element) {
      return;
    }

    if (isActive) {
      element.preload = 'auto';

      element.play().catch(() => {});
    } else {
      element.pause();

      try {
        element.currentTime = 0;
      } catch (_) {}
    }
  }, [isActive]);

  /*
   * Cleanup timers when this card is removed.
   */
  useEffect(() => {
    return () => {
      clearTimeout(leftTrayTimer.current);
      clearTimeout(tapTimer.current);
      clearTimeout(longPressTimer.current);
    };
  }, []);

  const revealLeftTray = () => {
    setShowLeftTray(true);

    clearTimeout(leftTrayTimer.current);

    leftTrayTimer.current = setTimeout(() => {
      setShowLeftTray(false);
    }, 5000);
  };

  const handleTimeUpdate = () => {
    const element = videoRef.current;

    if (
      !element ||
      !element.duration ||
      !isActive
    ) {
      return;
    }

    setProgress(
      (element.currentTime /
        element.duration) *
        100
    );
  };

  const triggerLike = useCallback(
    async () => {
      if (
        requireAuth &&
        requireAuth()
      ) {
        return;
      }

      setShowHeart(true);

      window.setTimeout(() => {
        setShowHeart(false);
      }, 900);

      if (!isLiked) {
        setIsLiked(true);
        setLikes((value) => value + 1);

        try {
          await videoAPI.likeVideo(
            video.id
          );
        } catch (_) {
          /*
           * Roll back optimistic UI if request fails.
           */
          setIsLiked(false);
          setLikes(
            (value) =>
              Math.max(0, value - 1)
          );
        }
      }
    },
    [
      isLiked,
      video.id,
      requireAuth
    ]
  );

  const handleTap = useCallback(
    () => {
      tapCount.current += 1;

      clearTimeout(tapTimer.current);

      tapTimer.current =
        setTimeout(() => {
          if (tapCount.current === 1) {
            const element =
              videoRef.current;

            if (!element) {
              return;
            }

            if (element.paused) {
              element
                .play()
                .catch(() => {});
            } else {
              element.pause();

              setShowPause(true);

              window.setTimeout(() => {
                setShowPause(false);
              }, 700);
            }
          } else if (
            tapCount.current >= 2
          ) {
            triggerLike();
          }

          tapCount.current = 0;
        }, 220);
    },
    [triggerLike]
  );

  const handleLongPressStart = () => {
    clearTimeout(longPressTimer.current);

    longPressTimer.current =
      setTimeout(() => {
        const element =
          videoRef.current;

        if (element) {
          element.pause();
        }
      }, 500);
  };

  const handleLongPressEnd = () => {
    clearTimeout(
      longPressTimer.current
    );

    const element =
      videoRef.current;

    if (
      element &&
      element.paused &&
      isActive
    ) {
      element.play().catch(() => {});
    }
  };

  const handleLike = async (event) => {
    event.stopPropagation();

    if (
      requireAuth &&
      requireAuth()
    ) {
      return;
    }

    const previousLiked = isLiked;
    const previousLikes = likes;

    /*
     * Optimistic update makes the button feel
     * immediate instead of waiting for the API.
     */
    setIsLiked(!previousLiked);

    setLikes(
      previousLiked
        ? Math.max(0, previousLikes - 1)
        : previousLikes + 1
    );

    try {
      if (previousLiked) {
        await videoAPI.unlikeVideo(
          video.id
        );
      } else {
        await videoAPI.likeVideo(
          video.id
        );
      }
    } catch (_) {
      setIsLiked(previousLiked);
      setLikes(previousLikes);
    }
  };

  const toggleMute = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const element =
      videoRef.current;

    if (!element) {
      return;
    }

    const nextMuted =
      !element.muted;

    element.muted = nextMuted;

    setIsMuted(nextMuted);
  };

  // Stall/hang recovery for mobile
  React.useEffect(() => {
    if (!isActive) return;
    const el = videoRef.current;
    if (!el) return;
    const onStall = () => {
      setTimeout(() => {
        if (!el.paused) { el.load(); el.play().catch(()=>{}); }
      }, 2000);
    };
    const onWait = () => {
      setTimeout(() => {
        if (el.readyState < 3 && !el.paused) el.play().catch(()=>{});
      }, 3000);
    };
    el.addEventListener('stalled', onStall);
    el.addEventListener('waiting', onWait);
    return () => {
      el.removeEventListener('stalled', onStall);
      el.removeEventListener('waiting', onWait);
    };
  }, [isActive]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: '#000',
        overflow: 'hidden'
      }}
    >
      <video
        ref={videoRef}
        src={
          isNearActive
            ? video.video_file
            : undefined
        }
        poster={
          video.thumbnail ||
          undefined
        }
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          background: '#000'
        }}
        loop
        muted={isMuted}
        playsInline
        autoPlay={isActive}
        preload={
          isActive
            ? 'auto'
            : 'metadata'
        }
        onTimeUpdate={
          isActive
            ? handleTimeUpdate
            : undefined
        }
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
      />

      {/* LEFT TOUCH ZONE */}
      <div
        onTouchStart={revealLeftTray}
        onMouseEnter={revealLeftTray}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 30,
          zIndex: 40
        }}
      />

      {/* RIGHT TOUCH ZONE */}
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 30,
          zIndex: 40
        }}
      />

      {/* MAIN TAP AREA */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 5,
          pointerEvents: 'none'
        }}
        onClick={handleTap}
        onMouseDown={
          handleLongPressStart
        }
        onMouseUp={
          handleLongPressEnd
        }
        onTouchStart={
          handleLongPressStart
        }
        onTouchEnd={
          handleLongPressEnd
        }
      />

      {/* LEFT TRAY */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform:
            'translateY(-50%)',
          zIndex: 100
        }}
      >
        <div
          style={{
            padding:
              '14px 10px',
            background:
              'transparent',
            backdropFilter:
              'blur(3px)',
            display: 'flex',
            flexDirection:
              'column',
            gap: 18,
            alignItems:
              'center',
            opacity:
              showLeftTray
                ? 1
                : 0.9
          }}
        >
          <ActionButtons
            video={video}
            likes={likes}
            isLiked={isLiked}
            onLike={handleLike}
            onComment={(event) => {
              event.stopPropagation();

              if (
                requireAuth &&
                requireAuth()
              ) {
                return;
              }

              setShowComments(true);
            }}
          />
        </div>
      </div>

      {/* BOTTOM INFO */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          padding:
            '10px 16px 76px',
          background:
            'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)'
        }}
      >
        <VideoInfo
          video={video}
          isMuted={isMuted}
          toggleMute={toggleMute}
        />
      </div>

      {/* HEART */}
      {showHeart && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 120,
            display: 'flex',
            alignItems:
              'center',
            justifyContent:
              'center',
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              fontSize: 90,
              animation:
                'heartPop 0.9s ease-out forwards'
            }}
          >
            ❤️
          </div>
        </div>
      )}

      {/* PAUSE */}
      {showPause && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 120,
            display: 'flex',
            alignItems:
              'center',
            justifyContent:
              'center',
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              background:
                'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems:
                'center',
              justifyContent:
                'center',
              fontSize: 28,
              color: '#fff'
            }}
          >
            ⏸
          </div>
        </div>
      )}

      {/* VIDEO CONTROLS */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: '50%',
          transform:
            'translateX(-50%)',
          zIndex: 140,
          display: 'flex',
          alignItems:
            'center',
          gap: 20,
          background:
            'rgba(0,0,0,0.35)',
          backdropFilter:
            'blur(6px)',
          borderRadius: 30,
          padding:
            '6px 20px'
        }}
        onClick={(event) =>
          event.stopPropagation()
        }
        onTouchEnd={(event) =>
          event.stopPropagation()
        }
      >
        {/* BACK 10 */}
        <button
          onClick={(event) => {
            event.stopPropagation();

            const element =
              videoRef.current;

            if (element) {
              element.currentTime =
                Math.max(
                  0,
                  element.currentTime -
                    10
                );
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            flexDirection:
              'column',
            alignItems:
              'center',
            gap: 1,
            touchAction:
              'manipulation'
          }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.2"
          >
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 .49-3.51" />
          </svg>

          <span
            style={{
              fontSize: 8,
              color:
                'rgba(255,255,255,0.7)'
            }}
          >
            10
          </span>
        </button>

        {/* PLAY / PAUSE */}
        <button
          onClick={(event) => {
            event.stopPropagation();

            const element =
              videoRef.current;

            if (!element) {
              return;
            }

            if (element.paused) {
              element
                .play()
                .catch(() => {});
            } else {
              element.pause();
            }
          }}
          style={{
            background:
              'rgba(255,255,255,0.15)',
            border:
              '1.5px solid rgba(255,255,255,0.5)',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems:
              'center',
            justifyContent:
              'center',
            cursor: 'pointer',
            touchAction:
              'manipulation',
            flexShrink: 0
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="#fff"
          >
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        </button>

        {/* FORWARD 10 */}
        <button
          onClick={(event) => {
            event.stopPropagation();

            const element =
              videoRef.current;

            if (element) {
              element.currentTime =
                Math.min(
                  element.duration || 0,
                  element.currentTime + 10
                );
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            flexDirection:
              'column',
            alignItems:
              'center',
            gap: 1,
            touchAction:
              'manipulation'
          }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.2"
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-.49-3.51" />
          </svg>

          <span
            style={{
              fontSize: 8,
              color:
                'rgba(255,255,255,0.7)'
            }}
          >
            10
          </span>
        </button>
      </div>

      {/* PROGRESS */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 150,
          height: 3,
          background:
            'rgba(255,255,255,0.15)',
          cursor: 'pointer'
        }}
        onClick={(event) => {
          event.stopPropagation();

          const element =
            videoRef.current;

          if (
            !element ||
            !element.duration
          ) {
            return;
          }

          const rect =
            event.currentTarget.getBoundingClientRect();

          element.currentTime =
            ((event.clientX -
              rect.left) /
              rect.width) *
            element.duration;
        }}
      >
        <div
          style={{
            width:
              `${progress}%`,
            height: '100%',
            background: '#fff'
          }}
        />
      </div>

      {showComments && (
        <CommentDrawer
          video={video}
          onClose={() =>
            setShowComments(false)
          }
        />
      )}

      <style>
        {`
          @keyframes heartPop {
            0% {
              transform: scale(0);
              opacity: 1;
            }

            50% {
              transform: scale(1.2);
              opacity: 1;
            }

            100% {
              transform: scale(1);
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

export default React.memo(VideoCard);